<template>
    <div class="sidebar-container" :class="{ 'toggle-mode': isSidebarToggleMode }">
        <div
            class="side-bar"
            ref="sidebarRef"
            tabindex="0"
            :inert="isOverlayDrawerHidden || undefined"
            :class="{ expanded: expandedForChildren, open: drawerOpen }"
            @mouseenter="openSideBar"
            @focusin="openSideBar"
            @focusout="handleFocusOut"
            @mouseleave="handleMouseLeave"
            @mousedown="onPointerDown"
            @mouseup="onPointerUp"
        >
            <div class="sidebar-scroll-container" @mousedown.stop @focusin.stop>
                <template v-for="(routeItem, routeItemIndex) in filteredPagesOnSidebar">
                    <sidebar-section
                        v-if="'pages' in routeItem"
                        :expanded="expandedForChildren"
                        :key="`route-section-${routeItemIndex}`"
                        :routeItem="routeItem"
                        :sectionOpen="sectionOpen"
                        @close="collapseSidebar"
                        @toggle:section="toggleSection"
                    />
                    <sidebar-item
                        v-else
                        :expanded="expandedForChildren"
                        :key="`route-item-${routeItemIndex}`"
                        :routeItem="routeItem"
                        @close="collapseSidebar"
                    />
                </template>
            </div>
        </div>
        <transition name="background-fade">
            <primevue-button
                v-show="drawerOpen"
                class="background-mask"
                type="button"
                @click="collapseSidebar"
                aria-label="Close menu"
                title="Close menu"
            />
        </transition>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import type { Ref } from 'vue';
import { useRoute } from 'vue-router';
import PrimevueButton from 'primevue/button';
import { ISidebarRoutesSectionOption, ISidebarRoutesSubSection } from '@/interfaces/routing/ISidebarRoutes.ts';
import SidebarItem from '@/components/layout/sidebar/SidebarItem.vue';
import { enactRoutes } from '@/routes/enactRoutes.ts';
import { assetsSidebarInfo } from '@/routes/sidebar/assetsSidebarInfo.ts';
import { marketsSidebarInfo } from '@/routes/sidebar/marketsSidebarInfo.ts';
import SidebarSection from '@/components/layout/sidebar/SidebarSection.vue';
import { useUIStore } from '@/stores/uiStore.ts';
import { useUserSettingsStore } from '@/stores/userSettingsStore.ts';
import { useUserAccessStore } from '@/stores/userAccessStore.ts';
import { storeToRefs } from 'pinia';

const uiStore = useUIStore();
const userSettingsStore = useUserSettingsStore();
const userAccessStore = useUserAccessStore();
const route = useRoute();
const { isMobileView } = storeToRefs(uiStore);

// state
const isExpanded = ref<boolean>(false);
const sectionOpen = ref<string | null>(null);

const sidebarRef = ref<HTMLElement | null>(null) as Ref<HTMLElement | null>;
const pointerDownInside = ref(false);

const isSidebarToggleMode = computed<boolean>(() => userSettingsStore.userSettings?.otherSettings.data.sideNavVisible === false);
const isHoverControlledSidebar = computed<boolean>(() => !isSidebarToggleMode.value && !isMobileView.value);

const drawerOpen = computed<boolean>(() => {
    if (isSidebarToggleMode.value) return uiStore.showNavSidebar;
    if (isMobileView.value) return uiStore.showNavSidebar;
    return isExpanded.value;
});
const isOverlayDrawerHidden = computed<boolean>(() => !isHoverControlledSidebar.value && !drawerOpen.value);
const expandedForChildren = computed<boolean>(() => {
    if (isSidebarToggleMode.value) return uiStore.showNavSidebar;
    if (isMobileView.value) return uiStore.showNavSidebar;
    return isExpanded.value;
});

const filteredPagesOnSidebar = computed<Array<ISidebarRoutesSectionOption | ISidebarRoutesSubSection>>(() => {
    const sideBarContent = getSideBarContent();
    if (!sideBarContent.length) return [];

    return sideBarContent.reduce((acc, routeItem) => {
        if ('pages' in routeItem) {
            const subpages = routeItem.pages;
            if (subpages.length > 0) {
                acc.push({ ...routeItem, pages: subpages });
            }
        } else if (!routeItem.requiresDevDashboardModules || userAccessStore.shortTermMarketsConfig?.hasAssetMonitoringDefaults === true) {
            acc.push(routeItem);
        }
        return acc;
    }, [] as Array<ISidebarRoutesSectionOption | ISidebarRoutesSubSection>);
});

function openSectionIfContainsActiveTab (): void {
    const activeSubSection = filteredPagesOnSidebar.value.find(item => {
        if ('pages' in item) {
            return item.pages.some(page => page.route === route.name);
        }
        return false;
    }) as ISidebarRoutesSubSection | undefined;

    if (activeSubSection) {
        sectionOpen.value = activeSubSection.sectionName;
    } else {
        sectionOpen.value = null;
    }
}

function openSideBar (): void {
    openSectionIfContainsActiveTab();

    // Toggle-mode and mobile drawers are explicitly toggled via the header button.
    if (!isHoverControlledSidebar.value) return;

    isExpanded.value = true;
}

function collapseSidebar (): void {
    if (isSidebarToggleMode.value || isMobileView.value) {
        uiStore.updateShowNavSidebar(false);
    }
    isExpanded.value = false;
    sectionOpen.value = null;
}

function toggleSection (sectionName: string): void {
    if (sectionOpen.value === sectionName) {
        sectionOpen.value = null;
    } else {
        sectionOpen.value = sectionName;
    }
}

function getSideBarContent (): Array<ISidebarRoutesSectionOption | ISidebarRoutesSubSection> {
    const currentRouteNames = route.matched.map(r => r.name);
    if (currentRouteNames.includes(enactRoutes.assets.name)) {
        return assetsSidebarInfo;
    }
    if (currentRouteNames.includes(enactRoutes.markets.name)) {
        return marketsSidebarInfo;
    }
    return [];
}

function handleFocusOut (event: FocusEvent): void {
    if (!isHoverControlledSidebar.value) return;

    const container = sidebarRef.value;
    if (!container) {
    // fallback: collapse if nothing to check
        collapseSidebar();
        return;
    }

    // relatedTarget is the element that is receiving focus
    const relatedTarget = event.relatedTarget as HTMLElement | null;

    if (relatedTarget && container.contains(relatedTarget)) {
    // focus moved *within* the sidebar -> do not collapse
        return;
    }

    // If pointer is down inside, ignore collapse (user is interacting)
    if (pointerDownInside.value) {
        return;
    }

    // Otherwise safe to collapse
    collapseSidebar();
}

function handleMouseLeave (): void {
    if (!isHoverControlledSidebar.value) return;

    // don't collapse if a pointerdown interaction is in progress
    if (pointerDownInside.value) return;
    // otherwise collapse immediately
    collapseSidebar();
}

function onPointerDown (): void {
    if (!isHoverControlledSidebar.value) return;
    pointerDownInside.value = true;
}
function onPointerUp (): void {
    if (!isHoverControlledSidebar.value) return;
    // small delay to ensure click completes and focus events settle
    window.setTimeout(() => {
        pointerDownInside.value = false;
    }, 0);
}

watch(() => uiStore.showNavSidebar, (newVal) => {
    if (newVal) {
        openSideBar();
    } else {
        collapseSidebar();
    }
});

</script>

<style lang="scss" scoped>
.sidebar-container {
    box-shadow: 0 2px 2px 0px var(--shadowColour);
    height: 100%;
    z-index: 5;
    user-select: none;
    background: var(--neutral200);
    overflow: visible;
    isolation: isolate;
    border-right: solid 1px var(--neutral300);
}

.side-bar {
    position: absolute;
    background: var(--neutral200);
    width: var(--sidebarWidth);
    border-right: solid 1px var(--neutral300);
    transition: width 0.2s ease-in-out;
    overflow-x: visible;
    overflow-y: hidden;
    z-index: 6;
    max-height: 100%;
    display: flex;
    flex-direction: column;

    &.expanded {
        width: calc(var(--sidebarWidth) + var(--sidebarWidthExtended));
        border-bottom: solid 1px var(--neutral300);

        .sidebar-scroll-container {
            overflow-y: auto;
        }
    }
}

.sidebar-scroll-container {
    overflow-x: hidden;
    overflow-y: hidden;
    height: 100%;
}

.sidebar-handle {
    display: none;
}

.background-mask {
    position: fixed;
    inset: 0;
    background-color: var(--neutral0);
    border: none;
    border-radius: 0;
    padding: 0;
    box-shadow: none;
    z-index: -1;
    pointer-events: none;
    opacity: 0.5;
    transition: opacity 0.3s ease-in-out;
}

.sidebar-container.toggle-mode {
    position: fixed;
    width: 0;
    height: 0;
    overflow: visible;
    background: transparent;
    border-right: none;
    box-shadow: none;

    .side-bar {
        position: fixed;
        top: var(--headerbarHeight);
        left: -5rem;
        height: calc(100vh - var(--headerbarHeight));
        max-height: calc(100vh - var(--headerbarHeight));
        width: min(100vw, calc(var(--sidebarWidth) + var(--sidebarWidthExtended)));
        border-bottom: solid 1px var(--neutral300);
        overflow: visible;
        transform: translateX(calc(-100% + var(--resizeButtonSize)));
        transition: transform 300ms;

        &.open {
            transform: translateX(var(--resizeButtonSize));
        }

        .sidebar-scroll-container {
            overflow-y: auto;
        }
    }

    .side-bar.expanded {
        width: min(100vw, calc(var(--sidebarWidth) + var(--sidebarWidthExtended)));
    }

    .background-mask {
        top: var(--headerbarHeight);
        left: 0;
        right: 0;
        bottom: 0;
        width: 100vw;
        height: calc(100vh - var(--headerbarHeight));
        pointer-events: auto;
    }
}

@include responsive(sm) {
    .sidebar-container:not(.toggle-mode) {
        background: transparent;
        border-right: none;
    }

    .sidebar-container:not(.toggle-mode) .side-bar {
        position: fixed;
        top: var(--headerbarHeight);
        left: -5rem;
        height: calc(100vh - var(--headerbarHeight));
        max-height: calc(100vh - var(--headerbarHeight));
        width: min(100vw, calc(var(--sidebarWidth) + var(--sidebarWidthExtended)));
        border-bottom: solid 1px var(--neutral300);
        overflow: visible;
        transform: translateX(calc(-100% + var(--resizeButtonSize)));
        transition: transform 300ms;

        &.open {
            transform: translateX(var(--resizeButtonSize));
        }

        .sidebar-scroll-container {
            overflow-y: auto;
        }
    }

    .sidebar-container:not(.toggle-mode) .side-bar.expanded {
        width: min(100vw, calc(var(--sidebarWidth) + var(--sidebarWidthExtended)));
    }

    .sidebar-handle {
        display: flex;
        align-items: center;
        justify-content: center;
        position: absolute;
        top: 50%;
        right: calc(var(--resizeButtonSize) * -1);
        transform: translateY(-50%);
        width: var(--resizeButtonSize);
        height: var(--resizeButtonSize);
        background: var(--neutral200);
        color: var(--primaryFont);
        border: solid 1px var(--lcpBlue);
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
        box-shadow: var(--shadow1);
    }

    .sidebar-container:not(.toggle-mode) .background-mask {
        top: var(--headerbarHeight);
        left: 0;
        right: 0;
        bottom: 0;
        width: 100vw;
        height: calc(100vh - var(--headerbarHeight));
        pointer-events: auto;
    }
}

.background-fade-enter-from,
.background-fade-leave-to {
    transition-delay: 0s;
    opacity: 0;
}

.background-fade-enter-to,
.background-fade-leave-from {
    transition-delay: 0.2s;
    opacity: 0.5;
}

</style>
